import { useMutation } from '@tanstack/react-query';
import { SourcingPurchaseParameters } from 'api-clients/monolith';
import { produce } from 'immer';
import { useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { updateCampaign } from '@/api-clients/update-campaign';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { useInvalidateCampaignQueries } from '@/hooks/use-invalidate-campaign-queries';
import { usePostHog } from '@/hooks/use-posthog';
import { type CampaignOpening } from '@/contexts/edit-campaign-context';
import { JOVEO } from '@/types/channels';
import { SOURCING_CURRENCY } from '@/utils/sourcing-billing';

import { CampaignParameters } from './types';
import { HiringTarget } from './use-hiring-target';
import { ReachSummary } from './use-reach-summary';

interface UseEditCampaignFormProps {
  sourcingPurchaseId: string;
  reachCampaignId?: string;
  hookForm: UseFormReturn<CampaignParameters>;
  isReach: boolean;
  hiringTarget: HiringTarget;
  reachSummary: ReachSummary;
  closeForm: () => void;
  // Budget as loaded, before this edit, for budget_edit_complete's
  // before/after/delta.
  budgetBefore?: number;
  channel?: string;
  campaignOpening?: CampaignOpening;
}

export function useEditCampaignForm({
  sourcingPurchaseId,
  reachCampaignId,
  hookForm,
  isReach,
  hiringTarget,
  reachSummary,
  closeForm,
  budgetBefore,
  channel,
  campaignOpening,
}: UseEditCampaignFormProps) {
  const intl = useIntl();
  const { channelSlug } = useParams();
  const { confirm } = useConfirmation();
  const { notifySuccess, notifyError } = useToaster();
  const invalidateCampaignQueries = useInvalidateCampaignQueries();
  const posthog = usePostHog();
  const { campaignBudget } = hiringTarget;
  // Set at submit: a save that changes no budget is no budget edit, and its
  // complete would have no budget_edit_start to pair with.
  const isBudgetEditRef = useRef(false);

  const {
    formState: { dirtyFields },
    getValues,
  } = hookForm;

  const trackBudgetEditComplete = (requestBody: SourcingPurchaseParameters) => {
    // The submitted body, not the live form: Reach sends the campaign's
    // aggregate budget, matching budgetBefore's scope, and an edit made
    // while the save is pending is not what was saved.
    const budgetAfter = requestBody.budget ?? budgetBefore ?? 0;
    const budgetDelta = budgetAfter - (budgetBefore ?? budgetAfter);
    posthog.capture('source_campaign:budget_edit_complete', {
      campaign_id: sourcingPurchaseId,
      opening_name: campaignOpening?.openingName,
      opening_id: campaignOpening?.openingId,
      channel,
      currency: SOURCING_CURRENCY,
      budget_before: budgetBefore,
      budget_after: budgetAfter,
      budget_delta: budgetDelta,
      budget_change_direction:
        budgetDelta > 0
          ? 'increase'
          : budgetDelta < 0
            ? 'decrease'
            : 'unchanged',
    });
  };

  const { mutate: updateCampaignParameters, isPending: isSubmitting } =
    useMutation({
      mutationFn: updateCampaign,
      onSuccess: async (_data, { requestBody }) => {
        if (isBudgetEditRef.current) trackBudgetEditComplete(requestBody);

        closeForm();
        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Campaign updated successfully!',
            description: 'Success toast after saving channel campaign settings',
          }),
        });
        await invalidateCampaignQueries();
      },
      onError: () => {
        notifyError({
          message: intl.formatMessage({
            defaultMessage: 'Failed to update campaign!',
            description: 'Error toast when campaign save API fails',
          }),
        });
      },
    });

  const submitForm = () => {
    // Get only changed keys, excluding target_applicants and target_hires
    const changedKeys: (keyof SourcingPurchaseParameters)[] = (
      Object.keys(dirtyFields) as (keyof CampaignParameters)[]
    ).filter(key => key !== 'target_applicants' && key !== 'target_hires');

    const changedFields = produce<SourcingPurchaseParameters>({}, draft => {
      changedKeys.forEach(key => {
        const value = getValues(key);

        if (isReach) {
          draft.reach_campaign ??= {};
          // @ts-expect-error: Type narrowing for reach_campaign
          draft.reach_campaign[key] = value;
        } else {
          // @ts-expect-error: Generic assignment
          draft[key] = value;
        }
      });

      if (isReach && reachCampaignId && draft.reach_campaign) {
        draft.reach_campaign.reach_campaign_id = reachCampaignId;
        draft.budget = reachSummary.perDayBudget;
        draft.total_budget = reachSummary.budget;
        draft.expected_cpa = reachSummary.expectedCpa;
        draft.expected_conversion = reachSummary.expectedConversion;
      }

      if (channelSlug === JOVEO) {
        if (dirtyFields.budget_type || dirtyFields.budget) {
          draft.budget = getValues('budget');
          draft.budget_type = getValues('budget_type');
          draft.total_budget = campaignBudget;
        }
        if (dirtyFields.end_at) {
          draft.total_budget = campaignBudget;
        }
      }
    });

    isBudgetEditRef.current = !!(dirtyFields.budget || dirtyFields.budget_type);
    updateCampaignParameters({
      sourcingPurchaseId,
      requestBody: changedFields,
    });
  };

  const onFormSubmit = async () => {
    const effectiveDirtyKeys = Object.keys(dirtyFields).filter(
      key => key !== 'target_applicants' && key !== 'target_hires',
    );
    if (effectiveDirtyKeys.length === 0) return;

    let shouldSubmit = true;

    // Show confirmation dialog if budget is changed
    if (dirtyFields.budget || dirtyFields.budget_type) {
      const confirmed = await confirm({
        title: intl.formatMessage({
          defaultMessage: 'Confirm budget change',
          description: 'Dialog title before applying a new campaign budget',
        }),
        description: intl.formatMessage({
          defaultMessage:
            'Are you sure you want to update the budget for this campaign? ',
          description:
            'Dialog body warning the user budget edits will take effect',
        }),
        confirmLabel: intl.formatMessage({
          defaultMessage: 'Yes, update',
          description: 'Primary button: proceed with budget change',
        }),
        cancelLabel: intl.formatMessage({
          defaultMessage: 'No, cancel',
          description: 'Secondary button: dismiss without changing budget',
        }),
      });

      shouldSubmit = confirmed;
    }

    if (shouldSubmit) submitForm();
  };

  return {
    onFormSubmit,
    isSubmitting,
  };
}
